import { apiService } from './api'
import { ApiResponse, PaginatedResponse } from '../types'

// Types for Mealmate integration
export interface Ingredient {
  name: string
  amount?: number
  unit?: string
  origin: 'natural' | 'animal' | 'synthetic'
  category: 'plant' | 'animal' | 'chemical' | 'mineral'
  description?: string
  transparencyInfo?: string
  isSynthetic?: boolean
  isGmo?: boolean
}

export interface DietaryClaims {
  isVegan: boolean
  isVegetarian: boolean
  isGlutenFree: boolean
  isDairyFree: boolean
  isNatural: boolean
  isSyntheticFree: boolean
}

export interface SynergyInteraction {
  ingredients: string[]
  type: 'positive' | 'negative'
  effect: 'bioavailability' | 'digestion' | 'energy' | 'toxicity'
  description: string
  scientificScore: number // 0-10, based on research strength
}

export interface FoodSynergy {
  score: number // 0-100 overall synergy score
  interactions: SynergyInteraction[]
  summary: string
  recommendation: 'highly_recommended' | 'neutral' | 'caution'
}

export interface Vendor {
  id: string
  name: string
  description: string
  logo: string
  rating: number
  deliveryTime: string
  deliveryFee: number
  minimumOrder: number
  cuisineTypes: string[]
  dietaryOptions: string[]
  contactInfo: {
    phone: string
    email: string
    website: string
    address: string
  }
  isActive: boolean
  operatingHours: {
    day: string
    open: string
    close: string
    isOpen: boolean
  }[]
}

export interface Meal {
  id: string
  vendorId: string
  name: string
  description: string
  image: string
  price: number
  category: string
  nutritionInfo: {
    calories: number
    protein: number
    carbs: number
    fat: number
    fiber: number
    sodium: number
  }
  ingredients: string[]
  detailedIngredients: Ingredient[]
  dietaryClaims: DietaryClaims
  synergyAnalysis?: FoodSynergy
  allergens: string[]
  dietaryTags: string[]
  prepTime: number
  spiceLevel: 'mild' | 'medium' | 'hot'
  isAvailable: boolean
  availability: {
    startTime: string
    endTime: string
    maxOrders: number
    currentOrders: number
  }
}

export interface MealSelection {
  mealId: string
  vendorId: string
  quantity: number
  specialInstructions?: string
  dietaryModifications?: string[]
  deliveryTime?: string
}

export interface OrderRequest {
  userId: string
  vendorId: string
  meals: MealSelection[]
  deliveryAddress: string
  deliveryTime: string
  paymentMethod: string
  totalAmount: number
  dietaryRestrictions: string[]
  specialInstructions?: string
}

export interface OrderResponse {
  orderId: string
  vendorOrderId: string
  status: 'pending' | 'confirmed' | 'preparing' | 'out-for-delivery' | 'delivered' | 'cancelled'
  estimatedDeliveryTime: string
  trackingUrl?: string
  vendorConfirmation: {
    confirmed: boolean
    confirmationCode: string
    estimatedPrepTime: number
  }
}

export interface VendorAvailability {
  vendorId: string
  isAcceptingOrders: boolean
  nextAvailableSlot: string
  currentLoad: number
  maxLoad: number
  estimatedPrepTime: number
  menuItems: {
    mealId: string
    available: boolean
    remainingQuantity: number
  }[]
}

export interface DietaryPreferences {
  restrictions: string[]
  allergies: string[]
  preferences: string[]
  calorieRange?: {
    min: number
    max: number
  }
  macroTargets?: {
    protein: number
    carbs: number
    fat: number
  }
}

class MealmateService {
  private baseUrl = '/partners'
  private wsConnection: WebSocket | null = null
  private availabilityCallbacks: Map<string, (availability: VendorAvailability) => void> = new Map()

  // Vendor management
  async getVendors(params?: {
    cuisine?: string[]
    dietary?: string[]
    rating?: number
    deliveryTime?: string
    page?: number
    limit?: number
  }): Promise<ApiResponse<PaginatedResponse<Vendor>>> {
    const queryParams = new URLSearchParams()
    if (params?.cuisine) params.cuisine.forEach(c => queryParams.append('cuisine', c))
    if (params?.dietary) params.dietary.forEach(d => queryParams.append('dietary', d))
    if (params?.rating) queryParams.set('rating', params.rating.toString())
    if (params?.deliveryTime) queryParams.set('deliveryTime', params.deliveryTime)
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.limit) queryParams.set('limit', params.limit.toString())

    return apiService.get(`${this.baseUrl}/vendors?${queryParams}`)
  }

  async getVendorById(vendorId: string): Promise<ApiResponse<Vendor>> {
    return apiService.get(`${this.baseUrl}/vendors/${vendorId}`)
  }

  async getVendorMeals(
    vendorId: string,
    params?: {
      category?: string
      dietary?: string[]
      maxPrice?: number
      available?: boolean
      page?: number
      limit?: number
    }
  ): Promise<ApiResponse<PaginatedResponse<Meal>>> {
    const queryParams = new URLSearchParams()
    if (params?.category) queryParams.set('category', params.category)
    if (params?.dietary) params.dietary.forEach(d => queryParams.append('dietary', d))
    if (params?.maxPrice) queryParams.set('maxPrice', params.maxPrice.toString())
    if (params?.available !== undefined) queryParams.set('available', params.available.toString())
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.limit) queryParams.set('limit', params.limit.toString())

    return apiService.get(`${this.baseUrl}/vendors/${vendorId}/meals?${queryParams}`)
  }

  async getMealById(mealId: string): Promise<ApiResponse<Meal>> {
    return apiService.get(`${this.baseUrl}/meals/${mealId}`)
  }

  // Order management
  async createOrder(orderRequest: OrderRequest): Promise<ApiResponse<OrderResponse>> {
    return apiService.post(`${this.baseUrl}/orders`, orderRequest)
  }

  async getOrderStatus(orderId: string): Promise<ApiResponse<OrderResponse>> {
    return apiService.get(`${this.baseUrl}/orders/${orderId}/status`)
  }

  async cancelOrder(orderId: string, _reason?: string): Promise<ApiResponse<void>> {
    return apiService.delete(`${this.baseUrl}/orders/${orderId}`)
  }

  // Real-time availability
  async getVendorAvailability(vendorId: string): Promise<ApiResponse<VendorAvailability>> {
    return apiService.get(`${this.baseUrl}/vendors/${vendorId}/availability`)
  }

  async getAllVendorsAvailability(): Promise<ApiResponse<VendorAvailability[]>> {
    return apiService.get(`${this.baseUrl}/vendors/availability`)
  }

  // Dietary preferences
  async getUserDietaryPreferences(userId: string): Promise<ApiResponse<DietaryPreferences>> {
    return apiService.get(`${this.baseUrl}/users/${userId}/dietary-preferences`)
  }

  async updateUserDietaryPreferences(
    userId: string,
    preferences: DietaryPreferences
  ): Promise<ApiResponse<DietaryPreferences>> {
    return apiService.put(`${this.baseUrl}/users/${userId}/dietary-preferences`, preferences)
  }

  // Real-time WebSocket connection for availability updates
  connectToAvailabilityUpdates(userId: string): void {
    if (this.wsConnection) {
      this.wsConnection.close()
    }

    const wsUrl = `${import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:3001'}/mealmate/availability/${userId}`
    this.wsConnection = new WebSocket(wsUrl)

    this.wsConnection.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'availability_update' && data.vendorId) {
          const callback = this.availabilityCallbacks.get(data.vendorId)
          if (callback) {
            callback(data.availability)
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    this.wsConnection.onerror = (error) => {
      console.error('WebSocket connection error:', error)
    }

    this.wsConnection.onclose = () => {
      console.log('WebSocket connection closed')
    }
  }

  disconnectFromAvailabilityUpdates(): void {
    if (this.wsConnection) {
      this.wsConnection.close()
      this.wsConnection = null
    }
  }

  subscribeToVendorAvailability(vendorId: string, callback: (availability: VendorAvailability) => void): void {
    this.availabilityCallbacks.set(vendorId, callback)
  }

  unsubscribeFromVendorAvailability(vendorId: string): void {
    this.availabilityCallbacks.delete(vendorId)
  }

  // Vendor integration utilities
  async validateVendorConnection(vendorApiUrl: string, vendorApiKey: string): Promise<ApiResponse<{
    valid: boolean
    vendorInfo: Partial<Vendor>
    menuSync: boolean
    orderSync: boolean
  }>> {
    return apiService.post(`${this.baseUrl}/vendors/validate-connection`, {
      vendorApiUrl,
      vendorApiKey
    })
  }

  async syncVendorMenu(vendorId: string): Promise<ApiResponse<{
    synced: boolean
    itemsAdded: number
    itemsUpdated: number
    itemsRemoved: number
  }>> {
    return apiService.post(`${this.baseUrl}/vendors/${vendorId}/sync-menu`)
  }

  // Order validation and compatibility checking
  async validateOrderCompatibility(
    vendorId: string,
    meals: MealSelection[]
  ): Promise<ApiResponse<{
    compatible: boolean
    issues: string[]
    suggestions: string[]
    estimatedPrepTime: number
    totalCalories: number
    dietaryScore: number
  }>> {
    return apiService.post(`${this.baseUrl}/vendors/${vendorId}/validate-order`, {
      meals
    })
  }

  // Health and nutrition analysis
  async analyzeMealNutrition(mealIds: string[]): Promise<ApiResponse<{
    totalNutrition: {
      calories: number
      protein: number
      carbs: number
      fat: number
      fiber: number
      sodium: number
    }
    dietaryBalance: {
      score: number
      recommendations: string[]
      concerns: string[]
    }
    allergenWarnings: string[]
  }>> {
    return apiService.post(`${this.baseUrl}/meals/analyze-nutrition`, {
      mealIds
    })
  }

  async coordinateTravelMeal(data: {
    travelType: 'flight' | 'hotel'
    partnerId: string
    referenceNumber?: string
    startDate: string
    endDate?: string
    location?: string
    mealFee?: number
    currency?: string
    metadata?: Record<string, any>
  }): Promise<ApiResponse<{ success: boolean, spec: any }>> {
    return apiService.post(`${this.baseUrl}/travel/coordinate`, data)
  }

  async toggleTravelMode(enabled: boolean): Promise<ApiResponse<{ success: boolean, travel_mode: boolean }>> {
    return apiService.patch(`${this.baseUrl}/travel/mode`, { enabled })
  }
}

export const mealmateService = new MealmateService()